// 3D Preview — renders the customer's garment design in a Three.js scene.
// Has two render paths, chosen at open time based on whether a real GLB
// model exists for the garment:
//
//   1. GLB MODE: if `apps/web/public/garments/<slug>.glb` exists, load it
//      with GLTFLoader, light it with a RoomEnvironment HDR for proper PBR
//      response, and project the user's design as a DecalGeometry onto the
//      garment's front-chest area. This is the production path once each
//      garment has a real model behind it.
//
//   2. FALLBACK (curved-plane) MODE: when no GLB is present yet, fall
//      back to a gently curved plane textured with the composite of
//      mockup + design (the same composite the 2D Designer shows). Same
//      ergonomics as the GLB path — drag to rotate, scroll to zoom — so
//      the UX is consistent regardless of model availability.
//
// Three.js + loaders are dynamic-imported on open so they never land in
// the main bundle. On close we dispose every geometry / material /
// texture and stop the animation loop, so opening + closing the modal
// repeatedly stays cheap.
//
// See `apps/web/public/garments/README.md` for the model spec.
import { useEffect, useRef, useState } from 'react'
import type * as THREE_NS from 'three'
import type { fabric } from 'fabric'

interface Props {
  open: boolean
  onClose: () => void
  canvas: fabric.Canvas | null
  // Product slug used to look up a matching GLB at /garments/<slug>.glb.
  // When unset or the GLB is missing the modal falls back to the curved-
  // plane render path.
  garmentSlug?: string | null
  // URL of the per-product (or per-location) mockup PNG. Used by the
  // fallback path; ignored when a GLB is loaded.
  mockupUrl?: string | null
  // Fallback colour for products that have no mockup yet. Used by the
  // fallback path; ignored when a GLB is loaded.
  fallbackColorHex?: string | null
  garmentName?: string
  locationName?: string
}

// Per-slug decal placement overrides. See public/garments/README.md.
// Position is the world-space anchor on the garment surface; size is the
// decal projection box (x = width, y = height, z = projection depth —
// larger z wraps the decal further around curved surfaces). Both in the
// units the GLB was authored in. Defaults below are sensible for a
// ~1.5-unit-tall tee posed front-facing.
interface DecalPlacement { position: [number, number, number]; size: [number, number, number] }
const DEFAULT_PLACEMENT: DecalPlacement = { position: [0, 0.30, 0.25], size: [0.6, 0.7, 0.4] }
const PLACEMENT_OVERRIDES: Record<string, DecalPlacement> = {
  // Hoodie print area sits a touch lower because of the kangaroo pocket.
  hoodie:      { position: [0, 0.15, 0.30], size: [0.6, 0.7, 0.4] },
  // Cap front face — small, off-centre vertical.
  hat:         { position: [0, 0.85, 0.40], size: [0.4, 0.2, 0.3] },
  // Polo: print sits slightly below the placket.
  polo:        { position: [0, 0.20, 0.25], size: [0.55, 0.55, 0.4] },
  // Long sleeve: same as tee.
  'long-sleeve': { position: [0, 0.30, 0.25], size: [0.6, 0.7, 0.4] },
}

// Visual constants — kept here so they're easy to tweak without digging
// into the render loop. Units are arbitrary Three.js world units.
const PANEL_W       = 4.0           // garment width (≈ shoulder span feel)
const PANEL_H       = 4.5           // garment height
const CURVE_DEPTH   = 0.5           // how much the panel bends forward
const PANEL_SEG     = 48            // tessellation for smooth curve
const BG_COLOR      = 0x121417      // studio-dark backdrop
const SHADOW_OPACITY = 0.35

// HEAD-check for an existing GLB. Caddy's SPA fallback (`try_files {path}
// /index.html`) means a missing GLB returns 200 + index.html bytes, not
// 404 — so we have to verify the Content-Type isn't HTML before deciding
// the model is real. We rely on content-type alone: Caddy hands out
// `model/gltf-binary` for real .glb files and `text/html; charset=utf-8`
// for the SPA fallback, which is a 100 % reliable discriminator and works
// for tiny placeholder GLBs too (an earlier content-length guard was
// rejecting our own sub-64-KB placeholders as if they were HTML).
async function garmentGlbExists(slug: string | null | undefined): Promise<string | null> {
  if (!slug) return null
  const url = `/garments/${slug}.glb`
  try {
    const r = await fetch(url, { method: 'HEAD' })
    if (!r.ok) return null
    const ct = (r.headers.get('content-type') || '').toLowerCase()
    if (ct.includes('text/html')) return null
    return url
  } catch {
    return null
  }
}

// Export just the user's design (text + images) with a transparent
// background, tightly cropped to the bounding box of visible objects.
// Print-area guide is hidden while exporting so the dashed outline
// doesn't get baked into the decal. Mirrors the logic the AI Preview
// modal already uses for the same reason.
function exportDesignOnly(designCanvas: fabric.Canvas): string {
  const objs = designCanvas.getObjects().slice()
  const hiddenByUs = new Set<fabric.Object>()
  objs.forEach((o) => {
    const isGuide = (o.type === 'rect' && Array.isArray((o as fabric.Rect).strokeDashArray))
                 || (o.type === 'text' && (o as fabric.Text).text === 'PRINT AREA')
    if (isGuide && o.visible !== false) { o.visible = false; hiddenByUs.add(o) }
  })
  designCanvas.discardActiveObject()
  designCanvas.requestRenderAll()
  const visible = objs.filter((o) => o.visible !== false && !hiddenByUs.has(o))
  let png: string
  if (visible.length === 0) {
    // No design yet — return a 1×1 transparent PNG so the decal mesh is
    // benign rather than absent.
    png = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAklEQVR4nGNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII='
  } else {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    visible.forEach((o) => {
      const r = o.getBoundingRect(true, true)
      minX = Math.min(minX, r.left); minY = Math.min(minY, r.top)
      maxX = Math.max(maxX, r.left + r.width); maxY = Math.max(maxY, r.top + r.height)
    })
    const PAD = 4
    const cw = designCanvas.getWidth(); const ch = designCanvas.getHeight()
    const left   = Math.max(0,  Math.floor(minX) - PAD)
    const top    = Math.max(0,  Math.floor(minY) - PAD)
    const right  = Math.min(cw, Math.ceil(maxX)  + PAD)
    const bottom = Math.min(ch, Math.ceil(maxY)  + PAD)
    const width  = Math.max(1, right - left)
    const height = Math.max(1, bottom - top)
    png = designCanvas.toDataURL({ format: 'png', multiplier: 2, left, top, width, height } as unknown as fabric.IDataURLOptions)
  }
  hiddenByUs.forEach((o) => { o.visible = true })
  designCanvas.requestRenderAll()
  return png
}

// Find the most likely "body" mesh in a loaded GLB scene — the biggest one
// by surface-area-ish heuristic (bounding box X*Y). Works whether the
// modeller named the mesh "body" or left it as "Mesh.001". Returns null
// for empty scenes; callers fall back to projecting onto every mesh.
function findBodyMesh(root: THREE_NS.Object3D, T: typeof THREE_NS): THREE_NS.Mesh | null {
  let best: THREE_NS.Mesh | null = null
  let bestArea = 0
  root.traverse((obj) => {
    const m = obj as unknown as { isMesh?: boolean; geometry?: THREE_NS.BufferGeometry } & THREE_NS.Mesh
    if (!m.isMesh || !m.geometry) return
    if (!m.geometry.boundingBox) m.geometry.computeBoundingBox()
    const box = m.geometry.boundingBox
    if (!box) return
    const sz = new T.Vector3(); box.getSize(sz)
    const area = sz.x * sz.y
    if (area > bestArea) { bestArea = area; best = m }
  })
  return best
}

// Auto-fit the perspective camera + OrbitControls target to the model's
// bounding box. Tuned so the garment sits a comfortable margin inside the
// frame at the starting camera angle.
function fitCameraToObject(
  obj: THREE_NS.Object3D,
  camera: THREE_NS.PerspectiveCamera,
  target: THREE_NS.Vector3,
  T: typeof THREE_NS,
): void {
  const box = new T.Box3().setFromObject(obj)
  const size = new T.Vector3(); box.getSize(size)
  const center = new T.Vector3(); box.getCenter(center)
  const maxDim = Math.max(size.x, size.y, size.z)
  const fov = camera.fov * (Math.PI / 180)
  const dist = maxDim / 2 / Math.tan(fov / 2)
  // 1.6× margin so the model isn't flush against the edges.
  camera.position.set(center.x, center.y + size.y * 0.05, center.z + dist * 1.6)
  camera.near = Math.max(0.01, dist / 100)
  camera.far  = dist * 50
  camera.updateProjectionMatrix()
  target.copy(center)
  camera.lookAt(target)
}

// Compose the mockup image and the Fabric design layer into a single PNG
// data URL. Mirrors the Designer's `object-contain` mockup placement so
// the composite reads exactly like the 2D view. Used by the fallback
// (curved-plane) render path; the GLB path uses just the design instead.
async function composeMockupAndDesign(
  designCanvas: fabric.Canvas,
  mockupUrl: string | null | undefined,
  fallbackColorHex: string | null | undefined,
): Promise<string> {
  const w = designCanvas.getWidth()
  const h = designCanvas.getHeight()
  // 2× for crispness in the 3D view (text rendered to a small canvas
  // texture-mapped to a larger world-space mesh otherwise looks soft).
  const SCALE = 2
  const cvW = Math.round(w * SCALE)
  const cvH = Math.round(h * SCALE)

  const off = document.createElement('canvas')
  off.width = cvW; off.height = cvH
  const ctx = off.getContext('2d')
  if (!ctx) throw new Error('Could not create compositor canvas')
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'

  // 1. Solid colour wash. Either the product colour (when mockup is missing)
  //    or transparent (mockup is the body and brings its own colour). The
  //    fabric texture in 3D needs SOMETHING opaque under the design — we
  //    fall back to white if both inputs are absent.
  if (!mockupUrl && fallbackColorHex) {
    ctx.fillStyle = fallbackColorHex
    ctx.fillRect(0, 0, cvW, cvH)
  } else if (!mockupUrl) {
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, cvW, cvH)
  }

  // 2. Mockup image, scaled to fit (CSS `object-contain`). Same fit logic
  //    used by the <img> behind the Designer canvas — width/height bounded,
  //    aspect preserved, centred.
  if (mockupUrl) {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image()
      el.crossOrigin = 'anonymous'
      el.onload = () => resolve(el)
      el.onerror = () => reject(new Error(`Could not load mockup: ${mockupUrl}`))
      el.src = mockupUrl
    })
    const scale = Math.min(cvW / img.naturalWidth, cvH / img.naturalHeight)
    const dw = img.naturalWidth * scale
    const dh = img.naturalHeight * scale
    const dx = (cvW - dw) / 2
    const dy = (cvH - dh) / 2
    ctx.drawImage(img, dx, dy, dw, dh)
  }

  // 3. Design layer on top, captured from Fabric at the same SCALE. The
  //    print-area guide stays hidden by the same logic the existing 2D
  //    preview uses, so we render at full-size with `multiplier: SCALE`.
  const designPng = designCanvas.toDataURL({ format: 'png', multiplier: SCALE })
  const designImg = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image()
    el.onload = () => resolve(el)
    el.onerror = () => reject(new Error('Could not load design layer'))
    el.src = designPng
  })
  ctx.drawImage(designImg, 0, 0, cvW, cvH)

  return off.toDataURL('image/png')
}

export default function ThreeDPreviewModal({ open, onClose, canvas, garmentSlug, mockupUrl, fallbackColorHex, garmentName, locationName }: Props): JSX.Element | null {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const cleanupRef = useRef<(() => void) | null>(null)
  const [autoRotate, setAutoRotate] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [ready, setReady] = useState(false)

  // ESC closes the modal.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // Set up the Three.js scene whenever the modal opens (and tear it down
  // when it closes or the canvas changes). The whole module is imported
  // lazily so the home/login paths never load three.
  useEffect(() => {
    if (!open) return
    let disposed = false
    setReady(false); setErr(null)

    ;(async () => {
      try {
        if (!canvas) throw new Error('Designer canvas is not ready yet')
        if (!containerRef.current) return

        // Decide which render path to use BEFORE doing any heavy module
        // imports — a missing GLB is cheap to detect.
        const glbUrl = await garmentGlbExists(garmentSlug)

        const THREE = await import('three')
        const { OrbitControls } = await import('three/examples/jsm/controls/OrbitControls.js')
        // GLB path needs GLTFLoader + DecalGeometry + RoomEnvironment for
        // proper PBR lighting; the fallback path doesn't, but the cost of
        // loading them eagerly here is small (a few KB gz) and keeps the
        // branch logic simpler.
        const { GLTFLoader }       = await import('three/examples/jsm/loaders/GLTFLoader.js')
        const { DecalGeometry }    = await import('three/examples/jsm/geometries/DecalGeometry.js')
        const { RoomEnvironment }  = await import('three/examples/jsm/environments/RoomEnvironment.js')

        if (disposed || !containerRef.current) return

        const container = containerRef.current
        const width = container.clientWidth
        const height = container.clientHeight

        const scene = new THREE.Scene()
        scene.background = new THREE.Color(BG_COLOR)

        const camera = new THREE.PerspectiveCamera(35, width / height, 0.1, 100)
        camera.position.set(0, 0.2, 6.4)

        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, powerPreference: 'high-performance' })
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
        renderer.setSize(width, height)
        renderer.outputColorSpace = THREE.SRGBColorSpace
        renderer.toneMapping = THREE.ACESFilmicToneMapping
        renderer.toneMappingExposure = 1.0
        renderer.shadowMap.enabled = true
        renderer.shadowMap.type = THREE.PCFSoftShadowMap
        container.appendChild(renderer.domElement)

        // --- Studio lighting rig (used by BOTH paths). Real PBR also gets
        // an IBL environment map below; the analytical lights stay for
        // shadows + rim highlights the env map alone won't give us.
        const ambient = new THREE.AmbientLight(0xffffff, 0.45)
        scene.add(ambient)

        const key = new THREE.DirectionalLight(0xffffff, 1.4)
        key.position.set(3, 4, 5)
        key.castShadow = true
        key.shadow.mapSize.set(1024, 1024)
        key.shadow.camera.near = 0.5
        key.shadow.camera.far = 20
        key.shadow.camera.left = -4
        key.shadow.camera.right = 4
        key.shadow.camera.top = 4
        key.shadow.camera.bottom = -4
        key.shadow.bias = -0.0005
        scene.add(key)

        const fill = new THREE.DirectionalLight(0x88aaff, 0.4)
        fill.position.set(-4, 2, 3)
        scene.add(fill)

        const rim = new THREE.DirectionalLight(0xffe0b0, 0.35)
        rim.position.set(0, 3, -5)
        scene.add(rim)

        // Indoor IBL from RoomEnvironment so PBR materials in GLBs have
        // realistic reflections / roughness response without us shipping
        // a separate HDR file.
        const pmrem = new THREE.PMREMGenerator(renderer)
        const envScene = new RoomEnvironment()
        const envTex = pmrem.fromScene(envScene, 0.04).texture
        scene.environment = envTex
        envScene.children.length = 0 // free the temp scene
        pmrem.dispose()

        // Disposable handles we'll need to track for cleanup regardless
        // of which render path runs.
        const disposables: Array<{ dispose: () => void }> = []
        disposables.push(envTex as unknown as { dispose: () => void })

        // ─────────────────────────────────────────────────────────────
        // Path A: GLB MODEL — load garment, project design as a decal
        // ─────────────────────────────────────────────────────────────
        const target = new THREE.Vector3(0, 0, 0)

        if (glbUrl) {
          const gltf = await new Promise<{ scene: THREE_NS.Group }>((resolve, reject) => {
            new GLTFLoader().load(glbUrl, resolve as (g: unknown) => void, undefined, reject)
          })
          if (disposed) return
          const model = gltf.scene as unknown as THREE_NS.Group
          // Cast every mesh in the model to cast + receive shadows so the
          // contact shadow on the floor plane below has something to catch.
          model.traverse((o) => {
            const m = o as { isMesh?: boolean } & THREE_NS.Mesh
            if (m.isMesh) { m.castShadow = true; m.receiveShadow = true }
          })
          scene.add(model)
          fitCameraToObject(model, camera, target, THREE)

          // Decal projection onto the largest body mesh in the model.
          const body = findBodyMesh(model, THREE)
          if (body) {
            const designPng = exportDesignOnly(canvas)
            const designTex = await new Promise<THREE_NS.Texture>((resolve, reject) => {
              new THREE.TextureLoader().load(designPng, (t) => {
                t.colorSpace = THREE.SRGBColorSpace
                t.anisotropy = renderer.capabilities.getMaxAnisotropy()
                resolve(t)
              }, undefined, reject)
            })
            disposables.push(designTex as unknown as { dispose: () => void })

            const place = (garmentSlug && PLACEMENT_OVERRIDES[garmentSlug]) || DEFAULT_PLACEMENT
            // Convert from world-local placement (authored in DEFAULT_PLACEMENT
            // assuming a roughly 1.5-unit-tall model) to the model's actual
            // bounding box so the decal lands sanely on small / large GLBs.
            const box = new THREE.Box3().setFromObject(model)
            const sz = new THREE.Vector3(); box.getSize(sz)
            const ctr = new THREE.Vector3(); box.getCenter(ctr)
            // Anchor relative to bbox: position.y treated as a fraction of
            // model height ABOVE the bbox centre (so 0.30 = 30 % of the
            // shirt height up from the centre, i.e. mid-chest). x stays
            // centred on the body's centre line.
            const anchor = new THREE.Vector3(
              ctr.x + place.position[0] * sz.x,
              ctr.y + place.position[1] * sz.y,
              ctr.z + place.position[2] * sz.z,
            )
            const sizeVec = new THREE.Vector3(
              place.size[0] * Math.max(sz.x, sz.y),
              place.size[1] * Math.max(sz.x, sz.y),
              place.size[2] * Math.max(sz.x, sz.y),
            )
            // Project along +Z (the model's "forward"). If a garment is
            // authored facing a different axis it'll need a per-slug
            // orientation override later — leaving that for Phase 2.
            const orientation = new THREE.Euler(0, 0, 0)
            const decalGeom = new DecalGeometry(body, anchor, orientation, sizeVec)
            const decalMat = new THREE.MeshStandardMaterial({
              map: designTex,
              transparent: true,
              alphaTest: 0.01,
              depthWrite: false,
              polygonOffset: true,
              polygonOffsetFactor: -4,
              roughness: 0.6,
              metalness: 0.0,
            })
            disposables.push(decalGeom as unknown as { dispose: () => void })
            disposables.push(decalMat  as unknown as { dispose: () => void })
            const decalMesh = new THREE.Mesh(decalGeom, decalMat)
            decalMesh.renderOrder = 1
            scene.add(decalMesh)
          }
        } else {
          // ───────────────────────────────────────────────────────────
          // Path B: FALLBACK — curved-plane composite (no GLB yet)
          // ───────────────────────────────────────────────────────────
          const composite = await composeMockupAndDesign(canvas, mockupUrl ?? null, fallbackColorHex ?? null)

          const geom = new THREE.PlaneGeometry(PANEL_W, PANEL_H, PANEL_SEG, PANEL_SEG)
          const pos = geom.attributes.position as THREE_NS.BufferAttribute
          for (let i = 0; i < pos.count; i++) {
            const x = pos.getX(i); const y = pos.getY(i)
            const bend = Math.cos((x / (PANEL_W / 2)) * (Math.PI / 2)) * CURVE_DEPTH
            const yFalloff = Math.cos((y / (PANEL_H / 2)) * (Math.PI / 4))
            pos.setZ(i, bend * yFalloff)
          }
          geom.computeVertexNormals()

          const tex = await new Promise<THREE_NS.Texture>((resolve, reject) => {
            new THREE.TextureLoader().load(composite, (t) => {
              t.colorSpace = THREE.SRGBColorSpace
              t.anisotropy = renderer.capabilities.getMaxAnisotropy()
              t.minFilter = THREE.LinearMipmapLinearFilter
              t.magFilter = THREE.LinearFilter
              t.generateMipmaps = true
              resolve(t)
            }, undefined, reject)
          })
          if (disposed) { tex.dispose(); return }

          const mat = new THREE.MeshStandardMaterial({
            map: tex, roughness: 0.85, metalness: 0.0, side: THREE.DoubleSide,
          })
          const panel = new THREE.Mesh(geom, mat)
          panel.castShadow = true
          panel.receiveShadow = true
          scene.add(panel)
          disposables.push(geom as unknown as { dispose: () => void })
          disposables.push(mat  as unknown as { dispose: () => void })
          disposables.push(tex  as unknown as { dispose: () => void })
          // For the fallback path the framing is fixed (no model to fit).
        }

        // Contact shadow ground — under whichever path's mesh got added.
        const shadowGeom = new THREE.PlaneGeometry(20, 20)
        const shadowMat = new THREE.ShadowMaterial({ opacity: SHADOW_OPACITY })
        const shadowPlane = new THREE.Mesh(shadowGeom, shadowMat)
        shadowPlane.rotation.x = -Math.PI / 2
        // Drop the shadow plane just below whatever's in the scene; for
        // the GLB path we used `target` as the focal centre and the
        // bbox.min is below that, so push down a touch.
        shadowPlane.position.y = (target.y || 0) - PANEL_H / 2 - 0.05
        shadowPlane.receiveShadow = true
        scene.add(shadowPlane)
        disposables.push(shadowGeom as unknown as { dispose: () => void })
        disposables.push(shadowMat  as unknown as { dispose: () => void })

        // OrbitControls — locked to a comfortable arc so the user can't
        // flip the garment upside-down or wander behind it.
        const controls = new OrbitControls(camera, renderer.domElement)
        controls.enableDamping = true
        controls.dampingFactor = 0.08
        controls.enablePan = false
        controls.target.copy(target)
        controls.minDistance = 1.5
        controls.maxDistance = 12
        controls.minPolarAngle = Math.PI * 0.25
        controls.maxPolarAngle = Math.PI * 0.75
        controls.minAzimuthAngle = -Math.PI * 0.6
        controls.maxAzimuthAngle =  Math.PI * 0.6
        controls.autoRotate = autoRotate
        controls.autoRotateSpeed = 0.8

        // The control state is read inside the loop; stash it on a ref-style
        // object so the auto-rotate toggle below can flip it without
        // unmounting / re-creating the scene.
        const ctrlState = { autoRotate }
        const setAR = (v: boolean) => { ctrlState.autoRotate = v; controls.autoRotate = v }

        // Render loop. Stops automatically when `disposed` is set by the
        // cleanup function below.
        let raf = 0
        const tick = () => {
          if (disposed) return
          controls.update()
          renderer.render(scene, camera)
          raf = requestAnimationFrame(tick)
        }
        tick()

        // Window resize — keep the canvas matching its container box.
        const onResize = () => {
          if (!containerRef.current) return
          const w = containerRef.current.clientWidth
          const h = containerRef.current.clientHeight
          camera.aspect = w / h
          camera.updateProjectionMatrix()
          renderer.setSize(w, h)
        }
        window.addEventListener('resize', onResize)

        // Stash the auto-rotate setter + cleanup on a ref so the React
        // surface can interact with it. Cleanup walks `disposables` so the
        // GLB and fallback paths share the same teardown — anything either
        // path created gets pushed into that array.
        ;(cleanupRef as { current: (() => void) | null }).current = () => {
          disposed = true
          cancelAnimationFrame(raf)
          window.removeEventListener('resize', onResize)
          controls.dispose()
          for (const d of disposables) {
            try { d.dispose() } catch { /* best-effort */ }
          }
          renderer.dispose()
          if (renderer.domElement.parentNode === container) {
            container.removeChild(renderer.domElement)
          }
        }
        ;(window as unknown as { __evrst3dSetAR?: (v: boolean) => void }).__evrst3dSetAR = setAR
        setReady(true)
      } catch (e) {
        if (!disposed) setErr((e as Error).message ?? 'Could not render 3D preview')
      }
    })()

    return () => {
      disposed = true
      const cleanup = cleanupRef.current
      cleanupRef.current = null
      if (cleanup) cleanup()
      delete (window as unknown as { __evrst3dSetAR?: (v: boolean) => void }).__evrst3dSetAR
    }
    // We intentionally don't depend on `autoRotate` here — toggling that
    // would tear the whole scene down. The toggle reaches into the scene
    // via the stashed setter above. The other deps ARE inputs to the
    // scene build: change the active garment slug / mockup / colour and
    // the scene rebuilds in place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canvas, garmentSlug, mockupUrl, fallbackColorHex])

  if (!open) return null

  function toggleRotate() {
    const next = !autoRotate
    setAutoRotate(next)
    const setter = (window as unknown as { __evrst3dSetAR?: (v: boolean) => void }).__evrst3dSetAR
    if (setter) setter(next)
  }

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col overflow-hidden" style={{ maxHeight: '90vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div>
            <div className="font-heading uppercase font-bold text-base text-ink tracking-wider">3D Preview</div>
            <div className="text-[11px] text-ink-3 mt-0.5">
              {garmentName ? <span>{garmentName}</span> : null}
              {garmentName && locationName ? <span> · </span> : null}
              {locationName ? <span>{locationName}</span> : null}
              {!garmentName && !locationName ? <span>Drag to rotate · scroll to zoom · ESC to close</span> : null}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={toggleRotate} className="text-xs text-ink-2 font-semibold hover:text-ink flex items-center gap-1.5">
              <span className={'inline-block w-2 h-2 rounded-full ' + (autoRotate ? 'bg-emerald-500' : 'bg-ink-3/40')} />
              Auto-rotate {autoRotate ? 'on' : 'off'}
            </button>
            <button onClick={onClose} className="text-ink-3 hover:text-ink text-xl leading-none" aria-label="Close">✕</button>
          </div>
        </div>

        {/* 3D canvas — fills the modal body. The container is a ref target
            for three; we don't render anything inside it from React. */}
        <div className="relative bg-[#121417]" style={{ height: '70vh' }}>
          <div ref={containerRef} className="absolute inset-0" />
          {!ready && !err && (
            <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">Building 3D scene…</div>
          )}
          {err && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
              <div className="text-red-300 text-sm">{err}</div>
              <button onClick={onClose} className="text-white/70 hover:text-white text-xs mt-3 underline">Close</button>
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="px-5 py-3 border-t border-border flex items-center justify-between text-[11px] text-ink-3">
          <div>Drag to rotate · scroll to zoom · the design and mockup match the 2D editor exactly.</div>
          <button onClick={onClose} className="btn-outline !px-4 !py-1.5 text-xs">Done</button>
        </div>
      </div>
    </div>
  )
}
